import React from 'react';
import gql from 'graphql-tag';
import {useQuery} from "@apollo/client";
import styled from 'styled-components';
import moment from "moment";
import {ScoreCard} from "../../../components/scorecard";
import {Card, Icon} from "@blueprintjs/core";
import {useHistory} from "react-router";
import {useWindowDimensions} from "../../../hooks";

const GAMES_QUERY = gql`
    query($teamId: Int!) {

        gamesForTeam(id: $teamId) {
            id
            isCancelled
            
            createdBy {
                id
                name
            }
            opponent {
                id
                name
            }
            
            score {
                homeTeamRuns
                awayTeamRuns
            }

            schedulingDetails {
                homeTeam {
                    id
                    name
                }
                awayTeam {
                    id
                    name
                }
                field {
                    id
                    name
                }
                dateTime
            }
            formHistory {
                id
                createdBy {
                    id
                    name
                }
                timeCreated
                approvalStatus

                ... on GameSchedulingForm {
                    gameDateTime
                    gameField {
                        id
                        name
                    }
                    isHomeGameForEnteringTeam
                }

                approvers {
                    team {
                        id
                        name
                    }
                    status
                    statusLastUpdated
                }
            }
            
            primaryUmpire {
                id
                firstName
                lastName
                phoneNumber
            }

            secondaryUmpire {
                id
                firstName
                lastName
                phoneNumber
            }
            
            details {
                homeTeamRecord
                awayTeamRecord
            }
        }
    }
`

const GameCardLayout = styled(Card)`
  min-width: 400px;
  max-width: 800px;
  
  padding: 0 15px 15px 15px;
  
  display: grid;
  
  @media only screen and (max-width: 800px) {
    grid-template-rows: 75px 25px 1fr auto 25px 25px 1fr auto;
    grid-template-columns: 1fr;
    
    grid-template-areas:
      'description'
      'prevH'
      'prev'
      'prevD'
      'divider'
      'nextH'
      'next'
      'nextD';
  }

  grid-template-rows: 75px 25px 1fr auto;
  grid-template-columns: 1fr 10px 1fr;
  
  grid-template-areas:
    'description . action'
    'prevH . nextH'
    'prev divider next'
    'prevD . nextD'
    ;
`

export const GameInfoCard = ({ teamId }) => {

  const { data } = useQuery(GAMES_QUERY, {
    variables: {
      teamId
    }
  })

  const { width } = useWindowDimensions()
  const history = useHistory()

  if (!data) return ''

  const { gamesForTeam } = data

  const games = gamesForTeam
    .filter(g => g.schedulingDetails)
    .sort((a, b) => moment(a.schedulingDetails.dateTime).isBefore(moment(b.schedulingDetails.dateTime)) ? -1 : 1)

  const now = moment()
  now.subtract({ hours: 1 })

  let previousGame = [...games].reverse().filter(game => game.schedulingDetails).filter(game => moment(game.schedulingDetails.dateTime).isBefore(now))
  let nextGame = [...games].filter(game => game.schedulingDetails).filter(game => moment(game.schedulingDetails.dateTime).isAfter(now))

  if (previousGame.length === 0) {
    previousGame = null
  } else {
    previousGame = previousGame[0]
  }

  if (nextGame.length === 0) {
    nextGame = null
  } else {
    nextGame = nextGame[0]
  }


  return (
    <div
      style={{width: '100%', display: 'flex', justifyContent: 'center', marginTop: '25px'}}
    >

      <GameCardLayout
        interactive={true}
        style={{width: '100%'}}
        onClick={() => {
          history.push({pathname: '/schedule', search: `?teamId=${teamId}`})
        }}
      >

        {width > 800 ? (
          <>
            <h2 style={{gridArea: 'description', fontWeight: '600'}}>Games</h2>
            <div style={{gridArea: 'action', display: 'flex', flexDirection: 'column', justifyContent: 'center'}}>
              <div style={{color: '#669EFF', cursor: 'pointer', width: '100%', display: 'flex', flexDirection: 'row-reverse'}}>
                <div>
                  <h3 style={{display: 'inline-block', textAlign: 'right', margin: 0, marginRight: '5px', padding: 0, fontWeight: '600'}}>All Games</h3>
                  <Icon icon={'arrow-right'} style={{marginBottom: '3px'}} />
                </div>
              </div>
            </div>
          </>
        ):(
          <div style={{gridArea: 'description', display: 'flex', justifyContent: 'space-between'}}>
            <h2 style={{fontWeight: '600'}}>Games</h2>

            <div style={{display: 'flex', flexDirection: 'column', justifyContent: 'center'}}>
              <div style={{color: '#669EFF', cursor: 'pointer', width: '100%', display: 'flex', flexDirection: 'row-reverse'}}>
                <div>
                  <h3 style={{display: 'inline-block', textAlign: 'right', margin: 0, marginRight: '5px', padding: 0, fontWeight: '600'}}>All Games</h3>
                  <Icon icon={'arrow-right'} style={{marginBottom: '3px'}} />
                </div>
              </div>
            </div>
          </div>
        )}

        <h3 style={{margin: 0, padding: 0, gridArea: 'prevH', fontWeight: '600'}}>Previous Game - <FormattedDate dateTime={previousGame?.schedulingDetails.dateTime}/></h3>
        <h3 style={{margin: 0, padding: 0, gridArea: 'nextH', textAlign: width > 800 ? 'right' : 'left', fontWeight: '600'}}>Next Game - <FormattedDate dateTime={nextGame?.schedulingDetails.dateTime}/></h3>

        <div style={{gridArea: 'divider', display: 'flex', justifyContent: 'center', padding: '0 5px', margin: width <= 800 ? '10px 0' : undefined, marginLeft: '3px'}}>
          <div style={{borderRight: width > 800 ? '1px dashed lightgrey' : undefined, borderTop: width > 800 ? undefined : '1px dashed lightgrey', width: '100%', height: '100%'}} />
        </div>

        {previousGame ? (
          <>
            <div style={{gridArea: 'prev'}}>
              <ScoreCard
                game={previousGame.schedulingDetails}
                score={previousGame.score}
                details={previousGame.details}
                isCancelled={previousGame.isCancelled}
              />
            </div>

            <div style={{gridArea: 'prevD'}}>
              <p style={{margin: 0, padding: 0, fontWeight: '300', textAlign: 'center', color: 'lightgrey'}}>{capitalStr(previousGame?.schedulingDetails.field.name)}</p>
              <p style={{margin: 0, padding: 0, fontWeight: '300', textAlign: 'center', color: 'lightgrey', marginTop: '3px'}}>-- Umpires --<br />{formatUmpires(previousGame?.primaryUmpire, previousGame?.secondaryUmpire)}</p>
            </div>
          </>
        ) : (
          <div style={{gridArea: 'prev', display: 'flex',flexDirection: 'column', justifyContent: 'center', height: '100%'}}>
            <h2 style={{textAlign: 'center', color: 'grey'}}>No Games</h2>
          </div>
        )}

        {nextGame ? (
          <>
          <div style={{gridArea: 'next'}}>
            <ScoreCard
              game={nextGame.schedulingDetails}
              score={nextGame.score}
              details={nextGame.details}
              isCancelled={nextGame.isCancelled}
            />
          </div>

          <div style={{gridArea: 'nextD', padding: '0 5px'}}>
              <p style={{margin: 0, padding: 0, fontWeight: '300', textAlign: 'center'}}>{capitalStr(nextGame.schedulingDetails.field.name)}</p>
              <p style={{margin: 0, padding: 0, fontWeight: '300', textAlign: 'center', marginTop: '3px'}}>-- Umpires --<br />{formatUmpires(nextGame.primaryUmpire, nextGame.secondaryUmpire)}</p>
            </div>
          </>
        ) : (
          <div style={{gridArea: 'next', display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%'}}>
            <h2 style={{textAlign: 'center', color: 'grey'}}>No Games</h2>
          </div>
        )}

      </GameCardLayout>
    </div>
  )
}

const FormattedDate = ({ dateTime }) => {
  const datePart = moment(dateTime).format('l')
  const timePart = moment(dateTime).format('h:mm a')

  if (!dateTime) return <span />

  return <span style={{fontWeight: '500'}}>{timePart} <span style={{color: 'grey'}}>&bull;</span> {datePart}</span>
}

const capitalStr = (word) => word.toLowerCase().replace(/\b\w/g, c => c.toUpperCase());

function formatUmpires(ump1, ump2) {
  let umpires = 'Unknown'
  if (ump1 && ump2) {
    umpires = `${ump1.firstName} ${ump1.lastName} (${ump1.phoneNumber}) and ${ump2.firstName} ${ump2.lastName} (${ump2.phoneNumber})`
  } else if (ump1) {
    umpires = `${ump1.firstName} ${ump1.lastName} (${ump1.phoneNumber})`
  } else if (ump2) {
    umpires = `${ump2.firstName} ${ump2.lastName} (${ump2.phoneNumber})`
  }

  return umpires
}
